import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {CurrentContextService} from '../../../core/services/security/current-context.service';
import {PaginatorService} from '../../../shared/services/paginator.service';
import {lastValueFrom, of, tap} from 'rxjs';
import {UserAccessService} from '../../../core/services/user-access.service';
import {UtilsService} from '../../../shared/services/utils.service';
import {
  GrantedModuleRole,
  GrantedUser,
  GrantForPortalRequest,
  RBACModule,
  RBACRole,
  RevokeGrant
} from '../../../core/model/user-access.model';
import {SelectionModel} from '@angular/cdk/collections';
import {MatTableDataSource} from '@angular/material/table';
import {catchError} from 'rxjs/operators';
import {LoadingService} from "../../../core/services/loading.service";

@Component({
  selector: 'app-settings-users',
  templateUrl: './settings-users.component.html',
  styleUrls: ['./settings-users.component.scss']
})
export class SettingsUsersComponent implements AfterViewInit {

  @ViewChild('paginator') paginator!: MatPaginator;
  filterForm: FormGroup;

  addUserToRoleForm: FormGroup;
  addingUserToRole: boolean = false;
  revokingUserFromRole: boolean = false;
  availableRoles: RBACRole[] = [];
  availableModules: RBACModule[] = [];
  availableRoleCodes = [
    "retailer_rbac_co_owner",
    "retailer_rbac_administrator",
    "retailer_rbac_shopkeeper",
  ];

  grantedModuleTableSelection = new SelectionModel<GrantedModuleRole>(false, []);
  selectedModule: number = 1;
  grantedModuleDataSource: MatTableDataSource<GrantedModuleRole> = new MatTableDataSource<GrantedModuleRole>([]);
  grantedRolesDisplayedColumns: string[] = ['id', 'module', 'role', 'description'];
  // grantedRolesDisplayedColumns: string[] = ['select', 'id', 'module', 'role', 'description']; -- use it when we want multipe


  userDisplayedColumns: string[] = ['select', 'userId', 'userFirstname', 'userLastname', 'userEmail'];
  userDataSource: MatTableDataSource<GrantedUser> = new MatTableDataSource<GrantedUser>([]);
  userTableSelection = new SelectionModel<GrantedUser>(true, []);
  totalPages: number = 0;
  totalElements: number = 0;

  constructor(private contextService: CurrentContextService,
              private paginatorService: PaginatorService,
              public utils: UtilsService,
              private userAccessService: UserAccessService,
              public loader: LoadingService
  ) {
    this.filterForm = new FormGroup({
      moduleCode: new FormControl([]),
    });

    this.addUserToRoleForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      roleCodes: new FormControl([], [Validators.required])
    });

    lastValueFrom(this.userAccessService.getAllRoles(this.contextService.getCurrentRetailer().id))
      .then((rolesList: RBACRole[]) => {
        this.availableRoles = rolesList.filter((role) => this.availableRoleCodes.includes(role.code));
      });
  }

  ngOnInit() {
    this.userDataSource = new MatTableDataSource<GrantedUser>([]);
    this.refreshData();
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
      this.paginator.page
        .pipe(
          tap(() => this.loadUserAccess()))
        .subscribe();
    }
  }

  refreshData(): void {
    this.userAccessService.getAll(`/${this.contextService.getCurrentRetailer()!.id}/granted-roles`, {
      size: '10',
      page: '0',
      sort: 'moduleName,asc'
    }).pipe(
      catchError(() => of([]))
    ).subscribe((value: any) => {
      this.totalPages = value.totalPages;
      this.totalElements = value.totalElements;
      this.grantedModuleDataSource = new MatTableDataSource<GrantedModuleRole>(value.content);
    });
  }

  setSelectedRoleUsers(row: GrantedModuleRole) {
    this.grantedModuleTableSelection.toggle(row);
    this.userTableSelection.clear();
    this.selectedModule = row.roleId;
    this.userDataSource = new MatTableDataSource<GrantedUser>(row.users)
  }

  addUserToRoles(): void {
    this.addingUserToRole = true;
  }

  revokeUserRole(): void {
    this.revokingUserFromRole = true;
  }

  saveAddUserToRole(): void {
    let request: GrantForPortalRequest = {
      retailerId: this.contextService.getCurrentRetailer().id,
      roleCodes: this.addUserToRoleForm.get('roleCodes')!.value,
      user: {
        email: this.addUserToRoleForm.get('email')!.value,
        firstname: this.addUserToRoleForm.get('firstName')!.value,
        lastname: this.addUserToRoleForm.get('lastName')!.value
      },
      offeringUuid: this.contextService.getCurrentOffering() ? this.contextService.getCurrentOffering()!.offeringUuid : null
    };
    lastValueFrom(this.userAccessService.saveAccessRequest(request))
      .then(() => {
        this.addingUserToRole = false;
        this.refreshData();
      })
      .catch((error: string) => {
        console.log(error);
      });
  }

  confirmRevoke(): void {
    let request: RevokeGrant[] = [];
    const offeringUuid = this.contextService.getCurrentOffering()!.offeringUuid;
    this.userTableSelection.selected
      .forEach((selectedGrantUser) => {
        request.push({
          grantId: selectedGrantUser.grantId,
          userId: selectedGrantUser.userId,
          offeringUuid: offeringUuid,
          principleId: this.grantedModuleTableSelection.selected[0].grantPrincipleId,
          roleId: this.grantedModuleTableSelection.selected[0].roleId
        });
      });
    lastValueFrom(this.userAccessService.revokeAccess(request))
      .then(() => {
        this.revokingUserFromRole = false;
        this.refreshData();
      })
      .catch((error: string) => {
        console.log(error)
      });
  }

  filterResults(): void {
    this.loadUserAccess();
  }

  private loadUserAccess() {
    const filters = [
      {name: 'moduleCode', val: this.filterForm.get('moduleCode')?.value},
    ];
    this.userAccessService.getAll(`/${this.contextService.getCurrentRetailer()!.id}/granted-roles`, {
      size: this.paginator.pageSize.toString(),
      page: this.paginator.pageIndex.toString(),
      sort: 'moduleName,asc'
    }, filters)
      .pipe(
        catchError(() => of([]))
      ).subscribe((value: any) => {
      this.totalPages = value.totalPages;
      this.totalElements = value.totalElements;
      this.grantedModuleDataSource = new MatTableDataSource<GrantedModuleRole>(value.content);
    });
  }

  pageChangeEvent($event: PageEvent): void {
    this.loadUserAccess();
  }

  getPanelType(): { icon: string } {
    return {icon: 'warn-icon'}
  }

  cancelRevoke(): void {
    this.revokingUserFromRole = false;
  }
}
